import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import DomainsView from './components/DomainsView.vue'
import DomainDetail from './components/DomainDetail.vue'
import LoginView from './components/LoginView.vue'
import RegisterView from './components/RegisterView.vue'
import CreateDomainView from './components/CreateDomainView.vue'
import ProfileView from './components/ProfileView.vue'
import DashboardView from './components/DashboardView.vue'
import ScanResultsView from './components/ScanResultsView.vue'
import AdminDashboardView from './components/admin/AdminDashboardView.vue'
import AdminSitesView from './components/admin/AdminSitesView.vue'
import AdminUsersView from './components/admin/AdminUsersView.vue'
import AdminPaymentsView from './components/admin/AdminPaymentsView.vue'
import AdminAIReviewView from './components/admin/AdminAIReviewView.vue'
import SubscriptionPlansView from './components/admin/SubscriptionPlansView.vue'
import EditSubscriptionPlanView from './components/admin/EditSubscriptionPlanView.vue'
import UserSubscriptionPlansView from './components/UserSubscriptionPlansView.vue'
import NotificationsView from './components/NotificationsView.vue'
import PaymentsView from './components/PaymentsView.vue'
import SubscribeView from './components/SubscribeView.vue'
import OAuthCallback from './components/OAuthCallback.vue'
import MobileRedirect from './components/MobileRedirect.vue'
import CheckEmailView from './components/CheckEmailView.vue'
import PasswordResetFailedView from './components/PasswordResetFailedView.vue'
import ResetPasswordView from './components/ResetPasswordView.vue'
import NotFoundView from './components/NotFoundView.vue'
import { getCurrentUser, clearCurrentUserCache } from './utils/api'

// Create environment configuration
// NOTE: VUE_APP_* environment variables are embedded into the client bundle at build time
// and will be visible in the production JavaScript files. This is expected behavior for Vue.js.
// Only use VUE_APP_* for public configuration (API URLs, Firebase config, etc.).
// Never put secrets, API keys, or sensitive tokens in VUE_APP_* variables.
const env = {
  apiUrl: process.env.NODE_ENV === 'production' 
    ? process.env.VUE_APP_API_URL 
    : '/api',
  appTitle: process.env.VUE_APP_TITLE || 'Segofy'
}

// Create router instance
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: DashboardView,
      meta: { requiresAuth: true }
    },
    {
      path: '/domains',
      name: 'Domains',
      component: DomainsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/domains/create',
      name: 'CreateDomain',
      component: CreateDomainView,
      meta: { requiresAuth: true }
    },
    {
      path: '/domains/:id',
      name: 'DomainDetail',
      component: DomainDetail,
      meta: { requiresAuth: true }
    },
    {
      path: '/domains/:id/scan-results/:scanId?',
      name: 'scan-results',
      component: ScanResultsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/profile',
      name: 'Profile',
      component: ProfileView,
      meta: { requiresAuth: true }
    },
    {
      path: '/notifications',
      name: 'Notifications',
      component: NotificationsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/subscriptions',
      name: 'UserSubscriptionPlans',
      component: UserSubscriptionPlansView,
      meta: { requiresAuth: true }
    },
    {
      path: '/payments',
      name: 'Payments',
      component: PaymentsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/subscribe/:planId',
      name: 'Subscribe',
      component: SubscribeView,
      meta: { requiresAuth: true }
    },
    {
      path: '/login',
      name: 'Login',
      component: LoginView,
      meta: { requiresGuest: true }
    },
    {
      path: '/register',
      name: 'Register',
      component: RegisterView,
      meta: { requiresGuest: true }
    },
    {
      path: '/auth/google/callback',
      name: 'OAuthCallback',
      component: OAuthCallback
    },
    {
      path: '/admin',
      component: AdminDashboardView,
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: '',
          redirect: '/admin/users'
        },
        {
          path: 'sites',
          name: 'AdminSites',
          component: AdminSitesView
        },
        {
          path: 'users',
          name: 'AdminUsers',
          component: AdminUsersView
        },
        {
          path: 'subscription-plans',
          name: 'SubscriptionPlans',
          component: SubscriptionPlansView
        },
        {
          path: 'subscription-plans/:id/edit',
          name: 'EditSubscriptionPlan',
          component: EditSubscriptionPlanView
        },
        {
          path: 'payments',
          name: 'AdminPayments',
          component: AdminPaymentsView
        },
        {
          path: 'ai-review',
          name: 'AdminAIReview',
          component: AdminAIReviewView
        }
      ]
    },
    {
      path: '/mobile/:pathMatch(.*)*',
      name: 'MobileRedirect',
      component: MobileRedirect
    },
    {
      path: '/check-email',
      name: 'CheckEmail',
      component: CheckEmailView
    },
    {
      path: '/password-reset-failed',
      name: 'PasswordResetFailed',
      component: PasswordResetFailedView
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPasswordView,
      meta: { requiresGuest: true }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: NotFoundView
    }
  ]
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // Skip auth check if we're just showing an error message
  if (to.query.error) {
    return next()
  }

  const getToken = () => {
    try {
      return localStorage.getItem('fic') || null
    } catch (_) {
      return null
    }
  }

  const token = getToken()

  // If navigating to login or register and token exists, redirect to domains
  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (token) {
      return next('/dashboard')
    }
    return next()
  }

  // If route requires auth and there's no token, redirect to login
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!token) {
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }
  }

  // If route requires admin, check admin status
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    if (!token) {
      // No token, show 404 page (don't reveal admin routes exist)
      return next({ name: 'NotFound' })
    }

    try {
      const user = await getCurrentUser()
      if (!user || user.isAdmin !== true) {
        // User is not admin, show 404 page
        return next({ name: 'NotFound' })
      }
    } catch (error) {
      console.error('Failed to verify admin status:', error)
      // Clear cache on error and show 404
      clearCurrentUserCache()
      return next({ name: 'NotFound' })
    }
  }

  // For all routes, continue navigation
  next()
})

const app = createApp(App)
app.provide('env', env)
app.use(router)  // Use the router
app.mount('#app')
